import { useAuth } from "m2m-components/storage/useM2mAuth"
import dayjs from "dayjs"
import { useMemo } from "react"
import {
    useApprovalRequestById,
    useCleanerProfile
} from "@/hooks/useApprovalRequest"
import { useOperation } from "@/hooks/useOperaion"
import { usePhotoTourByOperationIds } from "@/hooks/usePhotoTourByOperationId"
import { usePlacementAddressByOperationId } from "@/hooks/usePlacementAddressByOperationId"
import { usePlacementDetailsByOperationId } from "@/hooks/usePlacementDetailsByOperationId"
import { useSubContractActDocument } from "@/hooks/useSubContractActDocument"
import { useCheckinOnDateByOperationIds } from "@/hooks/useCheckinOnDateByOperationIds"
import { calcTotalUnitPrice } from "@/model/approvalRequest"
import { useTranslation } from "@/hooks/useTranslation"

export const useApprovalRequestDetailViewProps = ({
    approvalRequestId
}: {
    approvalRequestId: string
}) => {
    const { token } = useAuth()
    const { data, error } = useSubContractActDocument(token, approvalRequestId)

    const { data: approvalRequest } = useApprovalRequestById(approvalRequestId)
    const operationId = approvalRequest?.cleaningId
    const { data: operation } = useOperation(token, operationId)
    const { data: hasCheckinMap } = useCheckinOnDateByOperationIds(
        token,
        operationId ? [operationId] : []
    )
    const hasCheckinOnDate =
        (operationId && hasCheckinMap?.[operationId]?.hasCheckinOnDate) ?? false

    // TODO: リリース1ヶ月程度経ったら削除すること
    // https://github.com/matsuri-tech/m2m-cleaning/pull/3463
    // バックエンド側で書面の内容を作成してDBに保存するようにしたが、
    // これがリリースするより前に作られた承認リクエストの場合は、データが存在しないので404エラーとなる。
    // その場合はフォールバックして今まで通りフロント側で作る。
    // ただこの古い処理は見ての通りあらゆるAPIを叩く為、各所の負荷が高い。なるべく早く削除したい
    const dataFromScratch = useGetFromScratch(approvalRequestId)
    if (error?.status === 404) {
        return dataFromScratch
    }

    return {
        cleaningDate: data?.serviceDate,
        tourName: data?.tourName,
        placementName: data?.placementName,
        unitPrice: data?.unitPrice,
        orderingCompanyName: data?.orderingCompanyName,
        receivingCompanyName: data?.receivingCompanyName,
        executionDate: data?.executionDate,
        paymentDueDate: data?.paymentDueDate,
        inspectionDate: data?.inspectionCompletionDate,
        deadlineAt: operation?.deadlineAt,
        hasCheckinOnDate
    }
}

const useGetFromScratch = (approvalRequestId: string) => {
    const { token } = useAuth()
    const { t } = useTranslation()
    const { data: approvalRequest } = useApprovalRequestById(approvalRequestId)

    const operationId = approvalRequest?.cleaningId
    const { data: operation } = useOperation(token, operationId)
    const { data: hasCheckinMap } = useCheckinOnDateByOperationIds(
        token,
        operationId ? [operationId] : []
    )
    const hasCheckinOnDate =
        (operationId && hasCheckinMap?.[operationId]?.hasCheckinOnDate) ?? false

    const { data: tourByOperationId } = usePhotoTourByOperationIds(
        token,
        operationId ? [operationId] : []
    )
    const tour =
        operationId && tourByOperationId
            ? tourByOperationId[operationId]
            : undefined

    const { data: placementAddress } =
        usePlacementAddressByOperationId(operationId)

    const { data: placementDetailByOperationId } =
        usePlacementDetailsByOperationId(
            token,
            operationId ? [operationId] : []
        )
    const placementName: string | undefined = useMemo(() => {
        const placementDetail =
            placementDetailByOperationId && operationId
                ? placementDetailByOperationId[operationId]
                : undefined

        if (!placementDetail) return undefined

        const { placement, listing, commonArea } = placementDetail

        return placement.placementType === "commonArea"
            ? commonArea?.name
            : listing?.name
    }, [placementDetailByOperationId, operationId])

    const { data: profile } = useCleanerProfile(token)

    return {
        cleaningDate: operation?.cleaningDate,
        tourName: tour?.title ?? t("清掃"),
        placementName: `${placementName ?? "-"}\n${placementAddress?.address ?? "-"}`,
        unitPrice: calcTotalUnitPrice(approvalRequest),
        orderingCompanyName: "matsuri technologies株式会社",
        receivingCompanyName: profile?.name,
        executionDate: dayjs().format("YYYY-MM-DD"),
        paymentDueDate: dayjs(operation?.cleaningDate)
            .add(1, "month")
            .date(15)
            .format("YYYY-MM-DD"),
        inspectionDate: operation?.cleaningDate,
        deadlineAt: operation?.deadlineAt,
        hasCheckinOnDate
    }
}
